import React, {useContext} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import {useOtpGuard} from '@igp/shared/auth';
import {
    type ButtonColumn,
    type DataGridColumn,
    DataGridServer,
    type DataGridServerProps,
    type TextColumn,
    type ToggleColumn,
} from '@igp/shared/components/data-grid';
import {LocalizedText} from '@igp/shared/components/i18n';
import {ModalContext} from '@igp/shared/components/modal';
import {useAsyncAction} from '@igp/shared/redux/async-actions';

import {groups, useBoAgentAccessCheck} from '@access-control';

import {boUserActions} from '../actions';
import {useBoUserActions, useBoUserReset} from '../hooks';
import type {BoUserDataGridColumns, BoUserDataGridModel, BoUserViewModel, Roles} from '../types';

import {AddUserSuccessModal} from './AddUserSuccessModal';
import {DisableAccountConfirmationModal} from './DisableConfirmationModal';
import {EditUserForm} from './EditUserForm';
import {UnlinkAgentConfirmationModal} from './UnlinkAgentConfirmationModal';

const localized = defineMessages({
    editButtonLabel: {
        id: 'BoUserDataGrid_editButtonLabel',
        defaultMessage: 'Edit',
    },
    resetButtonLabel: {
        id: 'BoUserDataGrid_resetButtonLabel',
        defaultMessage: 'Reset Password',
    },
    accountStatusEnabled: {
        id: 'BoUserDataGrid_accountStatusEnabled',
        defaultMessage: 'Enabled',
    },
    accountStatusDisabled: {
        id: 'BoUserDataGrid_accountStatusDisabled',
        defaultMessage: 'Disabled',
    },
    resetSuccessfull: {
        id: 'BoUserDataGrid_resetSuccessfull',
        defaultMessage: 'Password was reset successfully',
    },
    unlinkAgentButtonLabel: {
        id: 'BoUserDataGrid_unlinkAgentButtonLabel',
        defaultMessage: 'Unlink Agent',
    },
});

const localizedHeaders = defineMessages<BoUserDataGridColumns>({
    username: {
        id: 'BoUserDataGrid_username',
        defaultMessage: 'Username',
    },
    firstName: {
        id: 'BoUserDataGrid_firstName',
        defaultMessage: 'First Name',
    },
    lastName: {
        id: 'BoUserDataGrid_lastName',
        defaultMessage: 'Last Name',
    },
    email: {
        id: 'BoUserDataGrid_email',
        defaultMessage: 'Email',
    },
    role: {
        id: 'BoUserDataGrid_role',
        defaultMessage: 'Role',
    },
    aal: {
        id: 'BoUserDataGrid_aal',
        defaultMessage: 'AAL',
    },
    agentPlayerId: {
        id: 'BoUserDataGrid_agentPlayerId',
        defaultMessage: 'Agent Player ID',
    },
    edit: {
        id: 'BoUserDataGrid_edit',
        defaultMessage: 'Edit',
    },
    resetPassword: {
        id: 'BoUserDataGrid_resetPassword',
        defaultMessage: 'Reset Password',
    },
    accountStatus: {
        id: 'BoUserDataGrid_accountStatus',
        defaultMessage: 'Account Status',
    },
    unlinkAgent: {
        id: 'BoUserDataGrid_unlinkAgent',
        defaultMessage: 'Unlink Agent',
    },
    uuid: {
        id: 'BoUserDataGrid_uuid',
        defaultMessage: 'UUID',
    },
});

function useBoUserColumns(columns: BoUserDataGridColumns[]): DataGridColumn<BoUserDataGridModel>[] {
    const {openModal, closeModal} = useContext(ModalContext);
    const {formatMessage} = useIntl();
    const canEdit = useBoAgentAccessCheck(editableColumnsResource);
    const {verifyOtp} = useOtpGuard();
    const {handleUpdateUserAccountStatus, handleUnlinkAgent} = useBoUserActions();
    const {handleResetPassword} = useBoUserReset({
        onSuccess: ({username, newPassword}) =>
            openModal({
                title: <LocalizedText label={localized.resetSuccessfull} />,
                body: <AddUserSuccessModal username={username} password={newPassword} />,
            }),
    });
    const uuid: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.id,
        field: 'id',
        title: localizedHeaders.uuid,
        width: 150,
        flex: 1,
    };
    const username: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.username,
        field: 'username',
        title: localizedHeaders.username,
        width: 150,
        flex: 1,
    };

    const firstName: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.firstName,
        field: 'firstName',
        title: localizedHeaders.firstName,
        width: 150,
        flex: 1,
    };

    const lastName: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.lastName,
        field: 'lastName',
        title: localizedHeaders.lastName,
        width: 150,
        flex: 1,
    };

    const email: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.email,
        field: 'email',
        title: localizedHeaders.email,
        width: 150,
        flex: 1,
    };

    const role: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.role?.name,
        field: 'role',
        title: localizedHeaders.role,
        width: 150,
        flex: 1,
    };

    const aal: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.aalGroup?.name,
        field: 'aalGroup',
        title: localizedHeaders.aal,
        width: 150,
        flex: 1,
    };

    const agentPlayerId: TextColumn<BoUserDataGridModel> = {
        type: 'text',
        valueGetter: model => model.playerId,
        field: 'playerId',
        title: localizedHeaders.agentPlayerId,
        width: 150,
        flex: 1,
    };

    const edit: ButtonColumn<BoUserDataGridModel> = {
        type: 'button',
        valueGetter: model => ({
            title: formatMessage(localized.editButtonLabel),
            onClick: () => {
                if (verifyOtp()) {
                    openModal({
                        body: <EditUserForm user={model} />,
                    });
                }
            },
        }),
        field: 'edit',
        title: localizedHeaders.edit,
        width: 80,
        flex: 1,
    };

    const resetPassword: ButtonColumn<BoUserDataGridModel> = {
        type: 'button',
        valueGetter: model => ({
            title: formatMessage(localized.resetButtonLabel),
            onClick: () => {
                if (verifyOtp()) {
                    handleResetPassword(model.id, model.username);
                }
            },
            disabled: model.isResetInProgress,
        }),
        field: 'resetPassword',
        title: localizedHeaders.resetPassword,
        width: 160,
        flex: 1,
    };

    const accountStatus: ToggleColumn<BoUserDataGridModel> = {
        type: 'toggle',
        valueGetter: model => ({
            value: !model.isAccountLocked,
            localizedValue: {
                true: formatMessage(localized.accountStatusEnabled),
                false: formatMessage(localized.accountStatusDisabled),
            },
            onToggle: (isUnlocked: boolean) => {
                if (verifyOtp()) {
                    const agentRole: Roles = 'Agents';
                    if (model.role?.name === agentRole && !isUnlocked) {
                        openModal({
                            body: (
                                <DisableAccountConfirmationModal
                                    onConfirm={() => {
                                        handleUpdateUserAccountStatus(model.id, !isUnlocked);
                                        closeModal();
                                    }}
                                    onCancel={closeModal}
                                />
                            ),
                        });
                    } else {
                        handleUpdateUserAccountStatus(model.id, !isUnlocked);
                    }
                }
            },
            disabled: !canEdit,
            inProgress: model.isAcoountLockInPorgress,
        }),
        field: 'isAccountLocked',
        title: localizedHeaders.accountStatus,
        width: 160,
        flex: 1,
    };

    const unlinkAgent: ButtonColumn<BoUserDataGridModel> = {
        type: 'button',
        valueGetter: model => ({
            title: formatMessage(localized.unlinkAgentButtonLabel),
            onClick: () => {
                if (verifyOtp()) {
                    openModal({
                        body: (
                            <UnlinkAgentConfirmationModal
                                username={model.username}
                                uid={model.playerId}
                                onConfirm={() => {
                                    handleUnlinkAgent(model.id, model.playerId);
                                    closeModal();
                                }}
                                onCancel={closeModal}
                            />
                        ),
                    });
                }
            },
            disabled: !model.playerId || model.unlinkInProgress,
        }),
        field: 'unlinkAgent',
        title: localizedHeaders.unlinkAgent,
        width: 140,
        flex: 1,
    };

    const config: Record<BoUserDataGridColumns, DataGridColumn<BoUserDataGridModel>> = {
        uuid,
        username,
        firstName,
        lastName,
        email,
        role,
        aal,
        agentPlayerId,
        edit,
        resetPassword,
        accountStatus,
        unlinkAgent,
    };

    return columns.map(column => config[column]);
}

type BoUserDataGridProps = Omit<DataGridServerProps<BoUserViewModel, BoUserDataGridColumns>, 'columns'> & {
    columnKeys: BoUserDataGridColumns[];
};

export function BoUserDataGrid({columnKeys, rows, ...props}: BoUserDataGridProps) {
    const columns = useBoUserColumns(columnKeys);

    const {idActionState: updateActionsState} = useAsyncAction({asyncAction: boUserActions.updateUserAccountStatus});

    const {noIdActionState: resetActionState} = useAsyncAction({asyncAction: boUserActions.resetPassword});
    const {idActionState: unlinkActionState} = useAsyncAction({asyncAction: boUserActions.unlinkAgent});
    const dataGridRows = rows.map<BoUserDataGridModel>(r => ({
        ...r,
        isAcoountLockInPorgress: updateActionsState[r?.id]?.isProgress,
        isResetInProgress: resetActionState?.isProgress,
        unlinkInProgress: unlinkActionState[r?.id]?.isProgress,
    }));

    return <DataGridServer {...props} rows={dataGridRows} columns={columns} />;
}

export const editableColumnsResource = defineAccessControlledResource({
    name: 'BoUserEditableColumns',
    rules: new GroupAccessRule([groups.adminsGroup]),
});
